/**
 * AppShell — v3 application shell.
 *
 * Composes:
 *   - Sidebar  (240/64px, left)
 *   - TopBar   (56px, across the top)
 *   - InfoPane (legacy slide-in detail panel, right)
 *   - <main>   page content
 *
 * The width-juggling for the InfoPane (legacy desktop / laptop
 * overlay) is preserved verbatim from the legacy Dashboard widget
 * so info-pane behaviour does not regress.
 *
 * widgets/app-shell is the single boundary-exempt widget — it is
 * the seam between the new layout and the legacy chrome that has
 * not been migrated yet (BlurScreen, InfoPane, the click-blocker).
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router';
import { useBreadcrumbsSync } from '@features/breadcrumbs';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { toggleIsInfoPaneExpanded } from 'redux_store';
import BlurScreen from '_legacy/components/Shared/BlurScreen';
import ContentWrapper from '@shared/ui/ContentWrapper';
import { ContentSkeleton, ErrorBoundary } from '@shared/ui';
import { InfoPane } from '@widgets/info-pane';
import { INFO_PANE_WIDTHS } from '@widgets/info-pane/lib/constants';
import theme from '@shared/config/theme';
import Sidebar from './Sidebar';
import TopBar from './TopBar';

// Tokens shared with the dashboard mockup for grid sizing.
const SIDEBAR_W = 240;
const SIDEBAR_W_COLLAPSED = 80;
const TOPBAR_H = 80;

const AppRoot = styled('div', {
  shouldForwardProp: prop => prop !== '$collapsed',
})(({ $collapsed }) => ({
  display: 'grid',
  // `minmax(0, 1fr)` on both axes. A bare `1fr` is `minmax(auto, 1fr)`:
  // the track refuses to shrink below the min-content width of its
  // widest item. Across, that item is the TopBar — three breadcrumbs of
  // up to 200px each plus the account switcher and the action buttons —
  // so below ~1200px the content column froze at that width and the
  // root's `overflow: hidden` cut the right edge off every page with no
  // way to scroll to it (the patient edit form on a tablet). Down, the
  // same rule keeps a tall table (MRT virtualiser, long Resources list)
  // from expanding the row past the viewport and producing an app-level
  // scrollbar instead of a scrollable inner table.
  gridTemplateColumns: `${$collapsed ? SIDEBAR_W_COLLAPSED : SIDEBAR_W}px minmax(0, 1fr)`,
  gridTemplateRows: `${TOPBAR_H}px minmax(0, 1fr)`,
  gridTemplateAreas: `
    'sidebar topbar'
    'sidebar content'
  `,
  // dvh accounts for iPadOS Safari's dynamic browser chrome (100vh
  // overflows there); the vh entry is the fallback for old engines.
  height: ['100vh', '100dvh'],
  overflow: 'hidden',
  position: 'relative', // anchor for InfoPaneOverlay
  background: 'var(--qhs-bg)',
  transition: 'grid-template-columns 250ms ease, background-color 220ms ease',
}));

// Intentionally a div: legacy App.jsx already owns the outer
// `<main>` element; nesting <main> inside <main> is invalid HTML.
const Main = styled('div', {
  shouldForwardProp: prop => prop !== '$infoPaneWidth',
})(({ $infoPaneWidth }) => ({
  gridArea: 'content',
  position: 'relative',
  overflow: 'hidden',
  height: '100%',
  minHeight: 0,
  width: `calc(100% - ${$infoPaneWidth}px)`,
  transition: 'width 250ms ease',
}));

const Content = styled('div')({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  background: 'var(--qhs-surface)',
  overflow: 'auto',
  transition: 'background-color 220ms ease',
});

const ClickBlocker = styled('div')({
  position: 'absolute',
  inset: 0,
  zIndex: 10,
  cursor: 'pointer',
  backgroundColor: 'rgba(0, 0, 0, 0.3)',
  backdropFilter: 'blur(2px)',
});

// Legacy InfoPane root has `display: contents`, so its inner Grid
// becomes a direct grid-child of AppRoot and gets auto-placed into
// an implicit third grid row. That row inherits the pane's
// `marginTop: 80` and produces an 80-px phantom strip below the
// content area. Wrapping InfoPane in a positioned overlay removes
// it from grid flow entirely.
// While a table is fullscreen the pane stretches to the viewport top
// (`top: 0`) — the fullscreen paper covers the TopBar, and keeping
// the 80-px offset left a clipped strip of it above the pane.
const InfoPaneOverlay = styled('div', {
  shouldForwardProp: prop => prop !== '$fullHeight',
})(({ $fullHeight }) => ({
  position: 'absolute',
  top: $fullHeight ? 0 : `${TOPBAR_H}px`,
  right: 0,
  bottom: 0,
  pointerEvents: 'none',
  zIndex: 20,
  '& > *': { pointerEvents: 'auto' },
}));

// Runs `useBreadcrumbsSync` from inside the inner ErrorBoundary so a
// crash in breadcrumb resolution (e.g. a missing `:tableName` in a
// route's `parentPath`) is caught and rendered as an in-shell
// ErrorState — not as a white-screen render abort that takes down
// the navigation chrome with it. React error boundaries cannot
// catch errors thrown by their own hooks, so the sync must live in
// a child component.
const BreadcrumbsSyncRunner = () => {
  useBreadcrumbsSync();
  return null;
};

const AppShell = ({ children }) => {
  const dispatch = useDispatch();
  const { isSideMenuPinned } = useSelector(state => state.appSettings);
  const { isInfoPaneExpanded, currentPage } = useSelector(state => state.infoPane);
  // Table fullscreen state — set by the shared QhsTable's toolbar
  // toggle (and reset on table unmount). Drives the InfoPane overlay
  // stretching to the viewport top.
  const isTableFullScreen = Boolean(useSelector(state => state.table?.settings?.isFullScreen));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { state } = useLocation();

  const location = useLocation();
  const navigate = useNavigate();

  // Optional artificial delay carried by route state (legacy AppRouter
  // uses it after creating a list item so the loader can settle).
  const delay = state?.delay ?? 0;
  const [delayed, setDelayed] = useState(Boolean(delay));

  useEffect(() => {
    if (location.search && /(^\?|&)bc=/.test(location.search)) {
      const cleaned = location.search.replace(/(^\?|&)bc=[^&]*/, '');
      navigate(location.pathname + (cleaned.startsWith('&') ? `?${cleaned.slice(1)}` : cleaned), {
        replace: true,
      });
    }
    // Run only on initial mount — subsequent navigations never produce
    // ?bc= in the new system.
    // eslint-disable-next-line
  }, []);

  // Intentionally empty deps: the delayed flag is only consulted on
  // mount (set once from route state); we don't want the timer to
  // restart when other route state re-renders the shell.
  useEffect(() => {
    if (!delayed) return undefined;
    const timer = setTimeout(() => setDelayed(false), delay);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line

  // Match legacy InfoPane width math: full-width on desktop when
  // expanded, narrow rail otherwise, zero when no detail page.
  let infoPaneWidth = 0;
  if (currentPage?.uuid) {
    infoPaneWidth =
      isInfoPaneExpanded && isDesktop ? INFO_PANE_WIDTHS.defaultExpanded : INFO_PANE_WIDTHS.collapsed;
  }

  const collapsed = !isSideMenuPinned;

  return (
    <BlurScreen>
      <AppRoot $collapsed={collapsed}>
        <Sidebar />
        <TopBar />

        <Main $infoPaneWidth={infoPaneWidth}>
          {isInfoPaneExpanded && !isDesktop && (
            <ClickBlocker
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                dispatch(toggleIsInfoPaneExpanded(false));
              }}
            />
          )}
          <Content>
            {delayed ? (
              <ContentWrapper>
                <ContentSkeleton delay={0} />
              </ContentWrapper>
            ) : (
              <ErrorBoundary fullHeight resetKey={location.pathname}>
                <BreadcrumbsSyncRunner />
                {children}
              </ErrorBoundary>
            )}
          </Content>
        </Main>

        <InfoPaneOverlay $fullHeight={isTableFullScreen}>
          <InfoPane />
        </InfoPaneOverlay>
      </AppRoot>
    </BlurScreen>
  );
};

AppShell.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export default AppShell;
